<script setup lang="ts">
    import { ChevronDownIcon } from '@heroicons/vue/16/solid';
    import { SidebarMenu, SidebarMenuItem, SidebarMenuButton, SidebarSeparator, SidebarMenuSub } from '@/shared/ui/Sidebar';
    import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/shared/ui/Collapsible';
    import { useSidebarLinks } from '../../composables/use-sidebar-links';
    import UiIcon from '@/components/Global/UiIcon/UiIcon.vue';
    import { RouterLink } from 'vue-router';
    import { useApplicationStore } from '@/stores/application.store';
    import EntitySelectionMenuItem from './EntitySelectionMenuItem.vue';
    import { useUserStore } from '@/stores/user.store';

    const applicationStore = useApplicationStore();
    const userStore = useUserStore();
    const { t } = useI18n();
    const { items } = useSidebarLinks();
    const route = useRoute();

    const openGroups = ref<Record<number, boolean>>({});

    // Expand the group holding the current page, also after a full page load where the route resolves after mount
    watch(
        [() => route.name, items],
        () => {
            items.value.forEach((item, index) => {
                if (item.children?.some((child: { to: { name: string } }) => child.to.name === route.name)) {
                    openGroups.value[index] = true;
                }
            });
        },
        { immediate: true },
    );
</script>
<template>
    <SidebarMenu>
        <template v-if="userStore.account?.type === 'client'">
            <EntitySelectionMenuItem />
            <SidebarSeparator />
        </template>

        <template
            v-for="(item, index) in items"
            :key="index"
        >
            <SidebarSeparator
                v-if="item.header"
                class="my-1"
            />

            <SidebarMenuItem>
                <Collapsible
                    v-if="item.children"
                    v-model:open="openGroups[index]"
                    class="group/collapsible"
                >
                    <CollapsibleTrigger as-child>
                        <SidebarMenuItem>
                            <SidebarMenuButton :tooltip="t(item.name)">
                                <UiIcon
                                    v-if="item.icon"
                                    :icon="item.icon"
                                    :size="16"
                                    color="secondary-gray"
                                />
                                <span>{{ t(item.name) }}</span>
                                <ChevronDownIcon class="ml-auto transition-transform group-data-[state=open]/collapsible:rotate-180" />
                            </SidebarMenuButton>
                        </SidebarMenuItem>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                        <SidebarMenuSub>
                            <SidebarMenuItem
                                v-for="(child, ci) in item.children"
                                :key="ci"
                            >
                                <SidebarMenuButton as-child>
                                    <RouterLink
                                        :to="child.to"
                                        :exact-active-class="'bg-background-secondary'"
                                    >
                                        {{ t(child.name) }}
                                    </RouterLink>
                                </SidebarMenuButton>
                            </SidebarMenuItem>
                        </SidebarMenuSub>
                    </CollapsibleContent>
                </Collapsible>

                <SidebarMenuButton
                    v-if="!item.children"
                    as-child
                    :tooltip="t(item.name)"
                >
                    <RouterLink
                        :to="{
                            name: item.to.name,
                            query: applicationStore.hasQueryParamsBasedUrl(item.to.name) as any,
                            params: item.to.params,
                        }"
                        :exact-active-class="'bg-background-secondary'"
                    >
                        <UiIcon
                            v-if="item.icon"
                            :icon="item.icon"
                            :size="16"
                            color="secondary-gray"
                        />
                        <span>{{ t(item.name) }}</span>
                    </RouterLink>
                </SidebarMenuButton>
            </SidebarMenuItem>
        </template>
    </SidebarMenu>
</template>
